import type { CBClient } from './client';

/**
 * Extrait les tokens OAuth depuis le hash de l'URL et les stocke dans la session.
 *
 * À appeler juste après le callback OAuth si detectSessionInUrl ne fonctionne pas.
 *
 * @param client - Client cb-core
 * @returns true si une session a été absorbée depuis l'URL
 *
 * @example
 * ```typescript
 * import { absorbOAuthCallback } from '@cb/sdk';
 *
 * // Au démarrage de l'app, juste après la création du client
 * const absorbed = await absorbOAuthCallback(client);
 * if (absorbed) {
 *   console.log('Session OAuth absorbée depuis l\'URL');
 * }
 * ```
 */
export async function absorbOAuthCallback(client: CBClient): Promise<boolean> {
  // Vérifier qu'on est dans un navigateur
  if (typeof window === 'undefined') {
    return false;
  }

  const hash = window.location.hash;

  // Vérifier si l'URL contient des paramètres OAuth
  if (!hash || !hash.includes('access_token')) {
    return false;
  }

  try {
    // Parser les paramètres du hash
    const hashParams = new URLSearchParams(hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');

    // Vérifier que les tokens essentiels sont présents
    if (!accessToken || !refreshToken) {
      console.warn('[cb-sdk] OAuth callback detected but missing tokens');
      return false;
    }

    // Stocker la session manuellement
    const { data, error } = await client.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });

    if (error) {
      console.error('[cb-sdk] Failed to set session from OAuth callback:', error);
      return false;
    }

    console.log('[cb-sdk] Session absorbed from OAuth callback:', data.session?.user?.email);

    // Nettoyer l'URL (enlever le hash)
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    return true;
  } catch (error) {
    console.error('[cb-sdk] Error absorbing OAuth callback:', error);
    return false;
  }
}
